import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auditsApi, sectorsApi, auditorsApi, sacpmApi } from '../api';
import { toast } from 'sonner';
import { extractApiError } from '../utils/dateValidation';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  ArrowLeft,
  Check,
  CheckSquare,
  Calendar,
  User,
  Users,
  Building2,
  ClipboardCheck,
  Save,
  Plus,
  Trash2,
  BadgeCheck,
  Clock,
  MessageSquare,
  Download,
  AlertTriangle,
  X,
} from 'lucide-react';

const STATUS_CONFIG = {
  planned: { label: 'Planejada', color: 'badge-gray' },
  scheduled: { label: 'Agendada', color: 'badge-blue' },
  in_progress: { label: 'Em Andamento', color: 'badge-yellow' },
  completed: { label: 'Concluída', color: 'badge-green' },
  cancelled: { label: 'Cancelada', color: 'badge-red' },
};

const AUDIT_TYPES = [
  { value: 'internal', label: 'Interna' },
  { value: 'external', label: 'Externa' },
  { value: 'supplier', label: 'Fornecedor' },
  { value: 'certification', label: 'Certificação' },
  { value: 'surveillance', label: 'Manutenção' },
  { value: 'process', label: 'Processo' },
];

const RESULT_LABELS = {
  conforming: { label: 'Conforme', color: 'badge-green' },
  non_conforming: { label: 'Não Conforme', color: 'badge-red' },
  improvement: { label: 'Melhorar', color: 'badge-yellow' },
};

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDateTimeBR = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR');
};

const CheckboxField = ({ checked, onChange, label, color }) => (
  <label
    className={`flex items-center gap-2 cursor-pointer select-none p-2 rounded-lg border transition-colors ${
      checked ? 'bg-green-500/10 border-green-500/40' : 'bg-gray-800/60 border-gray-700'
    }`}
  >
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-5 h-5 accent-green-500"
    />
    <span className="flex items-center gap-1 text-sm text-gray-300">
      <Check size={16} className={checked ? 'text-green-500' : 'text-gray-500'} />
      {label}
    </span>
    {checked && <span className="ml-auto text-green-500 text-xs font-semibold">Confirmado</span>}
  </label>
);

const SectorFields = ({
  draft,
  isSaving,
  onFieldChange,
  onSave,
  onDelete,
  auditors,
}) => (
  <div className="space-y-3">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Auditor */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
          <Users size={14} /> Auditor Responsável
        </label>
        {(() => {
          const currentName = draft.auditor_name || '';
          const matched = (auditors || []).some((a) => a.name === currentName);
          const extraOptions = currentName && !matched ? [currentName] : [];
          return (
            <select
              className="input w-full"
              value={currentName}
              onChange={(e) => onFieldChange('auditor_name', e.target.value)}
            >
              <option value="">Selecione...</option>
              {[...extraOptions, ...(auditors || []).map((a) => a.name)].map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          );
        })()}
        {(auditors || []).length === 0 && (
          <p className="text-xs text-gray-500 mt-1">
            Nenhum auditor cadastrado. Cadastre em <span className="text-green-400">Cadastros &gt; Auditores</span>.
          </p>
        )}
      </div>

      {/* Data e hora */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
          <Calendar size={14} /> Data e Hora
        </label>
        <input
          type="datetime-local"
          className="input w-full"
          value={toLocalInputValue(draft.scheduled_at)}
          onChange={(e) => onFieldChange('scheduled_at', e.target.value)}
        />
      </div>
    </div>

    {/* O que vai ser auditado */}
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
        <Building2 size={14} /> O que será auditado
      </label>
      <textarea
        className="input w-full min-h-[60px]"
        rows={2}
        placeholder="Descreva o que será auditado neste setor"
        value={draft.audited_subject || ''}
        onChange={(e) => onFieldChange('audited_subject', e.target.value)}
      />
    </div>

    {/* Observação */}
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
        <MessageSquare size={14} /> Observação
      </label>
      <textarea
        className="input w-full min-h-[60px]"
        rows={2}
        placeholder="Observações sobre a auditoria neste setor"
        value={draft.observation || ''}
        onChange={(e) => onFieldChange('observation', e.target.value)}
      />
    </div>

    {/* Conformidade */}
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
        <ClipboardCheck size={14} /> Conformidade
      </label>
      <select
        className="input w-full"
        value={draft.result || ''}
        onChange={(e) => onFieldChange('result', e.target.value)}
      >
        <option value="">Selecionar...</option>
        {Object.entries(RESULT_LABELS).map(([value, cfg]) => (
          <option key={value} value={value}>{cfg.label}</option>
        ))}
      </select>
    </div>

    {/* Visto */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <CheckboxField
        checked={draft.visto}
        onChange={(v) => onFieldChange('visto', v)}
        label="Visto"
        color="green"
      />
      {draft.visto_at && (
        <div className="flex items-center gap-2 text-xs text-gray-400 p-2">
          <Clock size={14} />
          Visto registrado em {formatDateTimeBR(draft.visto_at)}
        </div>
      )}
    </div>

    {/* Actions */}
    <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-700/60">
      <button
        onClick={onSave}
        disabled={isSaving}
        className="btn-primary flex items-center justify-center gap-2"
      >
        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        Salvar
      </button>
      <button
        onClick={onDelete}
        className="btn-danger flex items-center justify-center gap-2"
      >
        <Trash2 size={16} /> Remover
      </button>
    </div>
  </div>
);

const AuditAgenda = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();

  const [audit, setAudit] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [sectors, setSectors] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [newItem, setNewItem] = useState({ requirement: '', question: '', certificate_ids: [] });
  const [addingItem, setAddingItem] = useState(false);

  // Sector drafts per checklist item sector link
  const [sectorDrafts, setSectorDrafts] = useState({});
  const [savingSector, setSavingSector] = useState(null);
  const [addingSectorFor, setAddingSectorFor] = useState(null);
  const [newSectorId, setNewSectorId] = useState({});
  const [exporting, setExporting] = useState(false);
  const [pendingSacpm, setPendingSacpm] = useState(null);
  const [creatingSacpm, setCreatingSacpm] = useState(false);

  useEffect(() => {
    loadAll();
    loadSectors();
    loadAuditors();
  }, [auditId]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [auditRes, itemsRes] = await Promise.all([
        auditsApi.get(auditId),
        auditsApi.listChecklist(auditId),
      ]);
      setAudit(auditRes.data);
      setItems(itemsRes.data);
      seedSectorDrafts(itemsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar a agenda da auditoria');
    } finally {
      setLoading(false);
    }
  };

  const seedSectorDrafts = (loadedItems) => {
    const drafts = {};
    loadedItems.forEach((item) => {
      (item.sectors || []).forEach((sec) => {
        drafts[sec.id] = { ...sec };
      });
    });
    setSectorDrafts(drafts);
  };

  const loadSectors = async () => {
    try {
      const res = await sectorsApi.listActive();
      setSectors(res.data || []);
    } catch (error) {
      console.error('Error loading sectors:', error);
    }
  };

  const loadAuditors = async () => {
    try {
      const res = await auditorsApi.listActive();
      setAuditors(res.data || []);
    } catch (error) {
      console.error('Error loading auditors:', error);
    }
  };

  const toggleItem = (itemId) => {
    setExpanded((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleAddItem = async () => {
    if (!newItem.question.trim()) {
      toast.error('Preencha a pergunta do item');
      return;
    }
    setAddingItem(true);
    try {
      const res = await auditsApi.addChecklistItem(auditId, {
        ...newItem,
        sequence: items.length + 1,
        certificate_ids: newItem.certificate_ids.length ? newItem.certificate_ids : null,
      });
      setItems((prev) => [...prev, res.data]);
      setNewItem({ requirement: '', question: '', certificate_ids: [] });
      toast.success('Item adicionado');
    } catch (error) {
      toast.error('Erro ao adicionar item');
    } finally {
      setAddingItem(false);
    }
  };

  const toggleNewItemCertificate = (id) => {
    setNewItem((prev) => {
      const has = prev.certificate_ids.includes(id);
      return {
        ...prev,
        certificate_ids: has
          ? prev.certificate_ids.filter((c) => c !== id)
          : [...prev.certificate_ids, id],
      };
    });
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Excluir este item da lista de verificação?')) return;
    try {
      await auditsApi.deleteChecklistItem(itemId);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      toast.success('Item excluído');
    } catch (error) {
      toast.error('Erro ao excluir item');
    }
  };

  // ============== Sector management ==============

  const updateSectorDraft = (linkId, field, value) => {
    setSectorDrafts((prev) => ({ ...prev, [linkId]: { ...prev[linkId], [field]: value } }));
  };

  const handleAddSector = async (itemId) => {
    const sectorId = newSectorId[itemId];
    if (!sectorId) {
      toast.error('Selecione um setor');
      return;
    }
    setAddingSectorFor(itemId);
    try {
      const res = await auditsApi.addItemSector(itemId, { sector_id: sectorId });
      setItems((prev) =>
        prev.map((it) =>
          it.id === itemId ? { ...it, sectors: [...(it.sectors || []), res.data] } : it
        )
      );
      setSectorDrafts((prev) => ({ ...prev, [res.data.id]: { ...res.data } }));
      setNewSectorId((prev) => ({ ...prev, [itemId]: '' }));
      toast.success('Setor adicionado ao item');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar setor');
    } finally {
      setAddingSectorFor(null);
    }
  };

  const handleSaveSector = async (itemId, linkId) => {
    const draft = sectorDrafts[linkId];
    if (!draft) return;
    setSavingSector(linkId);
    try {
      const res = await auditsApi.updateItemSector(linkId, {
        auditor_name: draft.auditor_name || null,
        audited_subject: draft.audited_subject || null,
        observation: draft.observation || null,
        scheduled_at: draft.scheduled_at ? new Date(draft.scheduled_at).toISOString() : null,
        result: draft.result || null,
        visto: !!draft.visto,
      });
      setSectorDrafts((prev) => ({ ...prev, [linkId]: { ...res.data } }));
      setItems((prev) =>
        prev.map((it) =>
          it.id === itemId
            ? { ...it, sectors: (it.sectors || []).map((s) => (s.id === linkId ? res.data : s)) }
            : it
        )
      );
      toast.success('Setor atualizado com sucesso');
      if (draft.result === 'non_conforming' || draft.result === 'improvement') {
        const item = items.find((i) => i.id === itemId);
        setPendingSacpm({ item: item || {}, sector: res.data });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar setor');
    } finally {
      setSavingSector(null);
    }
  };

  const handleCreateSacpmFromAgenda = async (approved) => {
    const pending = pendingSacpm;
    setPendingSacpm(null);
    if (!approved || !pending || !audit) return;
    setCreatingSacpm(true);
    try {
      const item = pending.item || {};
      const sector = pending.sector || {};
      const sectorRef = sector.sector_name || sector.sector_code || '';
      const requirement = item.requirement ? `Requisito: ${item.requirement}\n` : '';
      const observation = sector.observation ? `Observação: ${sector.observation}\n` : '';
      const isImprovement = sector.result === 'improvement';
      const kindLabel = isImprovement ? 'melhoria' : 'não conforme';
      const description =
        `Item de auditoria ${kindLabel} (${audit.code})\n\n${requirement}Item: ${item.question || ''}\n` +
        `${sectorRef ? `Setor: ${sectorRef}\n` : ''}${observation}`.trim();
      const title = `${isImprovement ? 'Melhoria' : 'NC'} em Auditoria ${audit.code}: ${item.question || sectorRef}`.slice(0, 300);
      const res = await sacpmApi.create({
        title,
        description,
        problem_description: description,
        sacpm_type: isImprovement ? 'improvement' : 'corrective',
        origin: 'audit',
        area: sectorRef || null,
        audit_id: audit.id,
      });
      toast.success(`SACPM ${res.data.code} aberto a partir da auditoria`);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao abrir SACPM'));
    } finally {
      setCreatingSacpm(false);
    }
  };

  const handleDeleteSector = async (itemId, linkId) => {
    if (!window.confirm('Remover este setor do item?')) return;
    try {
      await auditsApi.deleteItemSector(linkId);
      setItems((prev) =>
        prev.map((it) =>
          it.id === itemId
            ? { ...it, sectors: (it.sectors || []).filter((s) => s.id !== linkId) }
            : it
        )
      );
      setSectorDrafts((prev) => {
        const next = { ...prev };
        delete next[linkId];
        return next;
      });
      toast.success('Setor removido');
    } catch (error) {
      toast.error('Erro ao remover setor');
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await auditsApi.exportAgendaPdf(auditId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `agenda_auditoria_interna_${audit?.code || 'agenda'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="animate-spin text-green-500" size={32} />
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p>Auditoria não encontrada.</p>
        <button onClick={() => navigate('/auditorias')} className="btn-secondary mt-4">
          <ArrowLeft size={16} className="mr-1" /> Voltar
        </button>
      </div>
    );
  }

  if (audit.audit_type !== 'internal') {
    return (
      <div className="text-center py-16 text-gray-400">
        <ClipboardCheck size={40} className="mx-auto mb-3 opacity-40" />
        <p>Esta auditoria não é interna e não possui lista de verificação.</p>
        <p className="text-sm mt-1">Documentos são anexados na ficha da auditoria.</p>
        <button onClick={() => navigate('/auditorias')} className="btn-secondary mt-4">
          <ArrowLeft size={16} className="mr-1" /> Voltar
        </button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[audit.status] || STATUS_CONFIG.planned;
  const typeLabel = AUDIT_TYPES.find((t) => t.value === audit.audit_type)?.label || audit.audit_type;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Link
            to="/auditorias"
            className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-green-400 transition-colors mb-2"
          >
            <ArrowLeft size={16} /> Voltar para Auditorias
          </Link>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            Agenda Auditoria Interna
          </h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="font-mono text-green-400">{audit.code}</span>
            <span className="text-white font-medium">{audit.title}</span>
          </div>
          <div className="flex gap-2 flex-wrap mt-2">
            <span className={`badge ${statusConfig.color}`}>{statusConfig.label}</span>
            <span className="badge badge-gray">{typeLabel}</span>
            {audit.planned_date && (
              <span className="badge badge-gray">
                <Calendar size={12} className="mr-1" />
                {new Date(audit.planned_date).toLocaleDateString('pt-BR')}
              </span>
            )}
            {audit.certificates && audit.certificates.length > 0 && (
              <span className="badge badge-purple flex items-center gap-1">
                <BadgeCheck size={12} />
                {audit.certificates.map((c) => c.name).join(' • ')}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPdf}
            disabled={exporting}
            className="btn-secondary flex items-center justify-center gap-2"
            title="Exportar Agenda em PDF"
          >
            {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            Exportar PDF
          </button>
          <button onClick={() => navigate('/auditorias')} className="btn-secondary">
            <ArrowLeft size={18} /> Voltar
          </button>
        </div>
      </div>

      {/* Add item */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-300 flex items-center gap-2">
            <ClipboardCheck size={18} /> Itens da Auditoria ({items.length})
          </p>
        </div>
        {(audit.certificates && audit.certificates.length > 0) && (
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-400 mb-2 flex items-center gap-1">
              <BadgeCheck size={12} /> Vincular a certificação(ões) (opcional)
            </p>
            <div className="flex gap-2 flex-wrap">
              {audit.certificates.map((cert) => {
                const selected = newItem.certificate_ids.includes(cert.id);
                return (
                  <label
                    key={cert.id}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-xs ${
                      selected
                        ? 'border-purple-500 bg-purple-500/20 text-purple-200'
                        : 'border-gray-700 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleNewItemCertificate(cert.id)}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500"
                    />
                    {cert.name}
                  </label>
                );
              })}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            className="input"
            placeholder="Requisito/Cláusula"
            value={newItem.requirement}
            onChange={(e) => setNewItem({ ...newItem, requirement: e.target.value })}
          />
          <input
            className="input"
            placeholder="Pergunta / Item de verificação *"
            value={newItem.question}
            onChange={(e) => setNewItem({ ...newItem, question: e.target.value })}
          />
          <button
            onClick={handleAddItem}
            disabled={addingItem}
            className="btn-primary col-span-1 md:col-span-2 flex items-center justify-center gap-1"
          >
            {addingItem ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            Adicionar Item
          </button>
        </div>
      </div>

      {/* Items */}
      {items.length === 0 ? (
        <div className="card p-8 text-center text-gray-400">
          <p>Nenhum item cadastrado na lista de verificação.</p>
          <p className="text-sm mt-1">Adicione um item acima para começar a preencher a agenda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map((item, idx) => {
            const isOpen = !!expanded[item.id];
            const itemSectors = item.sectors || [];
            const aggregateResult = itemSectors.some((s) => s.result === 'non_conforming')
              ? RESULT_LABELS.non_conforming
              : itemSectors.some((s) => s.result === 'improvement')
                ? RESULT_LABELS.improvement
                : itemSectors.some((s) => s.result === 'conforming')
                  ? RESULT_LABELS.conforming
                  : null;
            const allSeen = itemSectors.length > 0 && itemSectors.every((s) => s.visto);

            return (
              <div
                key={item.id}
                className={`card overflow-hidden transition-all duration-300 ${
                  isOpen ? 'ring-1 ring-green-500/40' : ''
                }`}
              >
                {/* Collapsed header - click to expand */}
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-700/30 transition-colors"
                  data-testid={`agenda-item-${item.id}`}
                >
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-700 text-white font-semibold text-sm shrink-0">
                    {idx + 1}
                  </span>
                  <span className="flex-1 min-w-0">
                    {item.requirement && (
                      <span className="block text-xs text-gray-400">{item.requirement}</span>
                    )}
                    <span className="block text-white text-sm font-medium truncate">
                      {item.question}
                    </span>
                    {(item.certificates && item.certificates.length > 0) && (
                      <span className="block mt-1 flex flex-wrap gap-1">
                        {item.certificates.map((c) => (
                          <span key={c.id} className="badge badge-purple !py-0 !px-2 text-[10px] inline-flex items-center gap-1">
                            <BadgeCheck size={10} /> {c.name}
                          </span>
                        ))}
                      </span>
                    )}
                    {itemSectors.length > 0 && (
                      <span className="block mt-1 flex flex-wrap gap-1">
                        {itemSectors.map((s) => (
                          <span
                            key={s.id}
                            className="badge badge-gray !py-0 !px-2 text-[10px] inline-flex items-center gap-1"
                          >
                            <Building2 size={10} /> {s.sector_name || s.sector_code}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                  {aggregateResult && <span className={`badge ${aggregateResult.color} hidden sm:inline-flex`}>{aggregateResult.label}</span>}
                  {allSeen && (
                    <span className="hidden sm:inline-flex text-green-500" title="Todos os setores com visto">
                      <CheckSquare size={18} />
                    </span>
                  )}
                  {isOpen ? (
                    <ChevronDown size={20} className="text-gray-400 shrink-0" />
                  ) : (
                    <ChevronRight size={20} className="text-gray-400 shrink-0" />
                  )}
                </button>

                {/* Expanded content */}
                <div
                  className={`grid transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="min-h-0">
                    <div className="px-4 pb-4 pt-2 border-t border-gray-700/60 space-y-4">
                      {/* Sectors */}
                      <div>
                        <p className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                          <Building2 size={16} /> Setores do item ({itemSectors.length})
                        </p>

                        {/* Add sector */}
                        <div className="flex flex-col sm:flex-row gap-2 mb-4">
                          <select
                            className="input sm:flex-1"
                            value={newSectorId[item.id] || ''}
                            onChange={(e) =>
                              setNewSectorId((prev) => ({ ...prev, [item.id]: e.target.value }))
                            }
                          >
                            <option value="">Selecione um setor...</option>
                            {sectors
                              .filter((s) => !itemSectors.some((sec) => sec.sector_id === s.id))
                              .map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.code} - {s.name}
                                </option>
                              ))}
                          </select>
                          <button
                            onClick={() => handleAddSector(item.id)}
                            disabled={addingSectorFor === item.id}
                            className="btn-secondary flex items-center justify-center gap-1"
                          >
                            {addingSectorFor === item.id ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Plus size={16} />
                            )}
                            Adicionar Setor
                          </button>
                        </div>

                        {/* Sector cards */}
                        {itemSectors.length === 0 ? (
                          <p className="text-sm text-gray-400">
                            Nenhum setor adicionado. Selecione um setor acima e clique em "Adicionar Setor".
                          </p>
                        ) : (
                          <div className="space-y-4">
                            {itemSectors.map((sec) => (
                              <div
                                key={sec.id}
                                className="border border-gray-700/70 rounded-xl p-4 bg-gray-800/40"
                              >
                                <div className="flex items-center gap-2 mb-3">
                                  <Building2 size={14} className="text-green-400" />
                                  <span className="text-sm font-semibold text-white">
                                    {sec.sector_name || sec.sector_code}
                                  </span>
                                  {sec.sector_code && (
                                    <span className="badge badge-gray">{sec.sector_code}</span>
                                  )}
                                </div>
                                <SectorFields
                                  draft={sectorDrafts[sec.id] || {}}
                                  isSaving={savingSector === sec.id}
                                  onFieldChange={(field, value) =>
                                    updateSectorDraft(sec.id, field, value)
                                  }
                                  onSave={() => handleSaveSector(item.id, sec.id)}
                                  onDelete={() => handleDeleteSector(item.id, sec.id)}
                                  auditors={auditors}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-700/60">
                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="btn-danger flex items-center justify-center gap-2"
                        >
                          <Trash2 size={16} /> Excluir Item
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SACPM confirmation modal */}
      {pendingSacpm && (
        <>
          <div className="modal-backdrop" onClick={() => !creatingSacpm && setPendingSacpm(null)} />
          <div className="modal-content p-6 max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2">
                <AlertTriangle size={18} className="text-red-400" />Abrir SACPM?
              </h3>
              <button onClick={() => !creatingSacpm && setPendingSacpm(null)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-300 text-sm">
              O setor foi marcado como <strong className="text-white">não conforme</strong> ou com <strong className="text-white">oportunidade de melhoria</strong>. Deseja abrir um SACPM com origem <strong className="text-white">Auditoria</strong>?
            </p>
            <div className="mt-3 bg-gray-800 rounded p-3 text-sm space-y-1">
              {pendingSacpm.item.requirement && <p className="text-xs text-gray-400">{pendingSacpm.item.requirement}</p>}
              {pendingSacpm.item.question && <p className="text-white">{pendingSacpm.item.question}</p>}
              <p className="text-xs text-gray-500">
                Setor: {pendingSacpm.sector.sector_name || pendingSacpm.sector.sector_code || '-'}
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setPendingSacpm(null)} disabled={creatingSacpm} className="btn-secondary">Não</button>
              <button onClick={() => handleCreateSacpmFromAgenda(true)} disabled={creatingSacpm} className="btn-primary flex items-center gap-2">
                {creatingSacpm && <Loader2 size={16} className="animate-spin" />}
                Abrir SACPM
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default AuditAgenda;
